import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  orderBy,
  limit,
  startAfter,
  where,
  getCountFromServer,
} from "firebase/firestore";
import { db } from "../config/firebase.config";
import { safeDate, safeNum, safeStr } from "../helper/utils.helper";
import { AVATAR_COL, PROJECTS_COL, USERS_COL } from "../helper/constants";

const PAGE_LIMIT = 12;

// ===== HELPERS =====
const getLastActiveDate = async (userId) => {
  if (!userId) throw new Error("User ID is required");

  const [projectsSnap, avatarSnap] = await Promise.all([
    getDocs(
      query(
        collection(db, PROJECTS_COL),
        where("userId", "==", userId),
        orderBy("updatedAt", "desc"),
        limit(1),
      ),
    ),
    getDocs(
      query(
        collection(db, AVATAR_COL),
        where("userId", "==", userId),
        orderBy("updatedAt", "desc"),
        limit(1),
      ),
    ),
  ]);

  const timestamps = [
    projectsSnap.docs[0]?.data()?.updatedAt?.toDate(),
    avatarSnap.docs[0]?.data()?.updatedAt?.toDate(),
  ].filter(Boolean);

  if (!timestamps.length) return null;
  return new Date(Math.max(...timestamps.map((t) => t.getTime())));
};

const getUserCounts = async (userId) => {
  if (!userId) throw new Error("User ID is required");

  const [projectsCount, avatarsCount, videosCount] = await Promise.all([
    getCountFromServer(
      query(collection(db, PROJECTS_COL), where("userId", "==", userId)),
    ),
    getCountFromServer(
      query(collection(db, AVATAR_COL), where("userId", "==", userId)),
    ),
    getCountFromServer(
      query(
        collection(db, PROJECTS_COL),
        where("userId", "==", userId),
        where("video", "!=", null),
      ),
    ),
  ]);

  return {
    projects: projectsCount.data().count,
    videos: videosCount.data().count,
    avatars: avatarsCount.data().count,
  };
};
// ===== EXPORTS =====
export const getAllUsers = async ({ lastDoc = null }) => {
  let q = query(
    collection(db, USERS_COL),
    orderBy("createdAt", "desc"),
    limit(PAGE_LIMIT),
  );

  if (lastDoc) {
    q = query(
      collection(db, USERS_COL),
      orderBy("createdAt", "desc"),
      startAfter(lastDoc),
      limit(PAGE_LIMIT),
    );
  }

  const snapshot = await getDocs(q);

  const users = await Promise.all(
    snapshot.docs.map(async (userDoc) => {
      const userId = userDoc.id;
      const data = userDoc.data();

      const [counts, latestActive] = await Promise.all([
        getUserCounts(userId),
        getLastActiveDate(userId),
      ]);

      return {
        userId,
        name: safeStr(data.name || data.displayName),
        email: safeStr(data.email),
        signedUp: safeDate(data.createdAt),
        credits: safeNum(data.credits),
        subscriptionId: data.subscriptionId || null,
        noOfProjects: counts.projects,
        noOfVideos: counts.videos,
        noOfCustomAvatars: counts.avatars,
        latestActive,
      };
    }),
  );

  return {
    users,
    lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
    hasMore: snapshot.docs.length === PAGE_LIMIT,
  };
};

export const getUserDetailById = async (userId) => {
  if (!userId) throw new Error("User ID is required");

  const [userSnap, counts, lastActive] = await Promise.all([
    getDoc(doc(db, USERS_COL, userId)),
    getUserCounts(userId),
    getLastActiveDate(userId),
  ]);

  if (!userSnap.exists()) throw new Error("User not found");

  const data = userSnap.data();

  return {
    userInfo: {
      userId: userSnap.id,
      name: safeStr(data.name || data.displayName),
      email: safeStr(data.email),
      credits: safeNum(data.credits),
      subscriptionId: data.subscriptionId || null,
      signedUp: safeDate(data.createdAt),
    },
    counts,
    lastActive,
    subscriptionId: data.subscriptionId || null,
  };
};

export const getUserById = async (userId) => {
  if (!userId) throw new Error("User ID is required");

  const userSnap = await getDoc(doc(db, USERS_COL, userId));

  if (!userSnap.exists()) return null;

  const data = userSnap.data();

  const [counts, latestActive] = await Promise.all([
    getUserCounts(userId),
    getLastActiveDate(userId),
  ]);

  return {
    userId: userSnap.id,
    name: safeStr(data.name || data.displayName),
    email: safeStr(data.email),
    signedUp: safeDate(data.createdAt),
    credits: safeNum(data.credits),
    subscriptionId: data.subscriptionId || null,
    noOfProjects: counts.projects,
    noOfVideos: counts.videos,
    noOfCustomAvatars: counts.avatars,
    latestActive,
  };
};
